import {booleanAttribute, ChangeDetectionStrategy, Component, computed, input} from "@angular/core";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {map, startWith, switchMap} from "rxjs";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatCheckbox} from "@angular/material/checkbox";
import {HtmlTekst, SpørsmålId, SpørsmålModell, Spørsmålstype, StorIkkeTomStreng} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {MatOption, MatSelect} from "@angular/material/select";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {caseForEnumMangler} from "felles-ng2/støttefunksjoner/enumer";
import {MatIcon} from "@angular/material/icon";
import {fadeInnAnimasjon} from "web-ng2/delt/animasjoner";
import {TranslateModule} from "@ngx-translate/core";
import {NgTemplateOutlet} from "@angular/common";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {MatTooltip} from "@angular/material/tooltip";

const debug = initDebugger('SpørreundersøkelseSpørsmålSkjemaKomponent');

export type SpørsmålSkjemaType = ReturnType<typeof SpørreundersøkelseSpørsmålSkjemaKomponent.opprettSkjema>;
export type SpørsmålSkjemaVerdi = SpørsmålSkjemaType['value'];

/**
 * Skjema for besvarelse av spørsmål i spørreundersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-spoersmaal-skjema',
    templateUrl: 'spørreundersøkelse-spørsmål-skjema.html',
    styleUrl: 'spørreundersøkelse-spørsmål-skjema.scss',
    host: {
        '[class.valgtittel-skjult]': '!visValgtittel()',
    },
    animations: [fadeInnAnimasjon('0.5s')],
    imports: [
        MatFormField,
        MatLabel,
        ReactiveFormsModule,
        MatCheckbox,
        MatRadioGroup,
        MatRadioButton,
        MatSelect,
        MatOption,
        MatIcon,
        TranslateModule,
        NgTemplateOutlet,
        RiktekstRedigeringKomponent,
        MatTooltip
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseSpørsmålSkjemaKomponent {

    static opprettSkjema(spørsmål: SpørsmålModell) {
        return new FormGroup({
            spoersmaal: new FormControl<SpørsmålModell>(spørsmål, {nonNullable: true}),
            svar: new FormControl<Svarverdi>(null)
        });
    }

    static somSpesifikasjon(skjema: SpørsmålSkjemaVerdi): SpørsmålSvarSpesifikasjon {
        return {
            spørsmålId: skjema.spoersmaal!.id,
            svar: SpørreundersøkelseSpørsmålSkjemaKomponent.svarSomStreng(skjema)
        }
    }

    static svarSomStreng(skjema: SpørsmålSkjemaVerdi): string|null {
        if (!SpørreundersøkelseSpørsmålSkjemaKomponent.erBesvart(skjema.svar)) return null;
        //spørsmål av typen velgNoenAvFlere lagres som en sortert kommaseparert liste med valgte verdier, eks "1,3"
        if (Array.isArray(skjema.svar)) return [...skjema.svar].sort((a, b) => a - b).join(',');
        return skjema.svar!.toString();
    }

    /** Sann dersom angitt svarverdi representerer et svar, dvs ikke er null og ikke er en tom liste (velgNoenAvFlere uten valgte alternativer) */
    static erBesvart(svar: Svarverdi | undefined): boolean {
        if (erNil(svar)) return false;
        if (Array.isArray(svar)) return svar.length > 0;
        return true;
    }

    static svarStrengverdiSomSvarverdi(spørsmål:SpørsmålModell, svar:string):Svarverdi{
        if (erNil(svar)) return null;
        switch (spørsmål.type) {
            case Spørsmålstype.grupperingstittel:
                return null;
            case Spørsmålstype.boolsk:
                return svar === 'true' ? true : false;
            case Spørsmålstype.fritekst:
                return svar;
            case Spørsmålstype.rangering:
                return parseInt(svar);
            case Spørsmålstype.velgEnAvFlere:
                return parseInt(svar);
            case Spørsmålstype.velgNoenAvFlere:
                return svar.split(',').map(verdi => parseInt(verdi));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(spørsmål.type);
                throw new Error(`ukjent Spørsmålstype ${spørsmål.type}`)
        }
    }

    skjemakontroll = input.required<SpørsmålSkjemaType>();
    /** Hvorvidt tittel for alternativene skal vises. Kun aktuell dersom spørsmålstype er av typen {@link Spørsmålstype#alternativer} */
    visValgtittel = input(true, {transform: booleanAttribute});
    /** Hvor tittel for alternativene skal plasseres. Kun aktuell dersom spørsmålstype er av typen {@link Spørsmålstype#alternativer} */
    valgtittelPlassering = input<ValgtittelPlassering>('under');

    spoersmaal = computed(() => this.skjemakontroll().value.spoersmaal!);
    /** skjemakontroll.svar castet til FormControl<HtmlTekst> for å unngå typefeil i html */
    spoersmaalsvarKontrollForHtmlTekst = computed(() => this.skjemakontroll().controls.svar as FormControl<HtmlTekst>);
    radioknappTittelplassering = computed(() => this.valgtittelPlassering() === 'over' ? 'before' : 'after');
    spoersmaalstyper = Spørsmålstype;

    /** Verdi og status for skjemakontroll.svar som signal, slik at visningen oppdateres også når svaret endres eller
     * skjemaet deaktiveres utenfra (eks når eksisterende besvarelse lastes) */
    private readonly svartilstand = toSignal(toObservable(this.skjemakontroll).pipe(
      switchMap(skjema => skjema.controls.svar.events.pipe(
        startWith(null),
        map(() => ({verdi: skjema.controls.svar.value, erDeaktivert: skjema.controls.svar.disabled}))
      ))
    ), {initialValue: {verdi: null, erDeaktivert: false}});

    /** Sann dersom spørsmålet er besvart, se {@link SpørreundersøkelseSpørsmålSkjemaKomponent#erBesvart} */
    erBesvart = computed(() => SpørreundersøkelseSpørsmålSkjemaKomponent.erBesvart(this.svartilstand().verdi));

    /** Verdiene til valgte alternativer. Kun aktuell for spørsmål av typen {@link Spørsmålstype#velgNoenAvFlere} */
    valgteVerdier = computed<number[]>(() => {
        const verdi = this.svartilstand().verdi;
        return Array.isArray(verdi) ? verdi : [];
    });
    /** Maksimalt antall alternativer som kan velges, null dersom det ikke er noen begrensning. Kun aktuell for spørsmål av typen {@link Spørsmålstype#velgNoenAvFlere} */
    maksimaltAntallValg = computed(() => this.spoersmaal().oppsett.velgNoenAvFlere?.maksimaltAntallValg ?? null);
    erMaksimaltAntallValgNaadd = computed(() => {
        const maks = this.maksimaltAntallValg();
        return maks !== null && this.valgteVerdier().length >= maks;
    });

    constructor() {

    }

    erValgt(verdi: number): boolean {
        return this.valgteVerdier().includes(verdi);
    }

    /** Sann dersom skjemaet er deaktivert, eller alternativet ikke er valgt og maksimalt antall valg er nådd */
    kanIkkeVelges(verdi: number): boolean {
        return this.svartilstand().erDeaktivert || (!this.erValgt(verdi) && this.erMaksimaltAntallValgNaadd());
    }

    /** Velger eller fjerner valg av alternativ. Valgte verdier holdes i samme rekkefølge som alternativene, og svaret settes til null dersom ingen alternativer er valgt */
    velg(verdi: number, valgt: boolean): void {
        const valgteVerdier = this.spoersmaal().oppsett.velgNoenAvFlere!.alternativer
          .map(alternativ => alternativ.verdi)
          .filter(kandidat => kandidat === verdi ? valgt : this.erValgt(kandidat));
        const svar = this.skjemakontroll().controls.svar;
        svar.setValue(valgteVerdier.length > 0 ? valgteVerdier : null);
        svar.markAsDirty();
        svar.markAsTouched();
    }
}

/** Svarverdi i skjemaet, number[] benyttes for spørsmål av typen velgNoenAvFlere */
export type Svarverdi = null | StorIkkeTomStreng | number | boolean | number[];
export type ValgtittelPlassering = 'over' | 'under';

export interface SpørsmålSvarSpesifikasjon {
    spørsmålId: SpørsmålId;
    svar: string | null;
}
